import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSwipeable } from "react-swipeable";
import products from "../data/products";

function productCopy(t, id) {
  const features = t(`products.items.${id}.features`, { returnObjects: true });
  return {
    tab: t(`products.items.${id}.tab`),
    kind: t(`products.items.${id}.kind`),
    name: t(`products.items.${id}.name`),
    description: t(`products.items.${id}.description`),
    features: Array.isArray(features) ? features : [],
  };
}

const Products = () => {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const [active, setActive] = useState(0);
  const [selected, setSelected] = useState(null);
  const [imageIndex, setImageIndex] = useState(0);
  const dialogRef = useRef(null);
  const product = products[active];
  const copy = productCopy(t, product.id);
  const selectedCopy = selected ? productCopy(t, selected.id) : null;

  useEffect(() => {
    const index = products.findIndex((item) => `#${item.id}` === hash);
    if (index >= 0) setActive(index);
  }, [hash]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || !selected || dialog.open) return;
    dialog.showModal();
  }, [selected]);

  const closeModal = () => {
    if (dialogRef.current?.open) dialogRef.current.close();
    setSelected(null);
  };

  const openModal = () => {
    setImageIndex(0);
    setSelected(product);
  };

  const step = (delta) => {
    if (!selected) return;
    const count = selected.images.length;
    setImageIndex((current) => (current + delta + count) % count);
  };

  const swipe = useSwipeable({
    onSwipedLeft: () => step(1),
    onSwipedRight: () => step(-1),
    trackMouse: true,
  });

  const onTabKey = (event) => {
    if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    let next = active;
    if (event.key === "ArrowRight") next = (active + 1) % products.length;
    if (event.key === "ArrowLeft") next = (active - 1 + products.length) % products.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = products.length - 1;
    setActive(next);
    document.getElementById(`product-tab-${next}`)?.focus();
  };

  return (
    <section id="products" className="products">
      <div className="wrap">
        <h2>
          <span>{t("products.kicker")}</span>
          {t("products.headline")}
        </h2>
        <div className="tablist" role="tablist" aria-label={t("products.tabs")} onKeyDown={onTabKey}>
          {products.map((item, index) => (
            <button
              key={item.id}
              id={`product-tab-${index}`}
              type="button"
              role="tab"
              aria-selected={index === active}
              aria-controls="product-panel"
              tabIndex={index === active ? 0 : -1}
              onClick={() => setActive(index)}
            >
              {t(`products.items.${item.id}.tab`)}
            </button>
          ))}
        </div>

        <div
          id="product-panel"
          role="tabpanel"
          aria-labelledby={`product-tab-${active}`}
          className="product-panel"
          key={product.id}
        >
          <div className="product-copy">
            <p className="kind">{copy.kind}</p>
            <h3>{copy.name}</h3>
            <p>{copy.description}</p>
            <ul className="feature-list">
              {copy.features.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
            <button type="button" className="link-go" onClick={openModal}>
              {t("products.view")}
            </button>
          </div>
          <div className="product-shot">
            <img src={product.images[0]} alt={copy.name} />
          </div>
        </div>
      </div>

      <dialog
        ref={dialogRef}
        className="product-dialog"
        aria-labelledby="product-dialog-title"
        onClose={() => setSelected(null)}
        onClick={(event) => {
          if (event.target === dialogRef.current) closeModal();
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowRight") step(1);
          if (event.key === "ArrowLeft") step(-1);
        }}
      >
        {selected && selectedCopy && (
          <>
            <div className="dialog-bar">
              <button type="button" className="icon-btn" aria-label={t("products.close")} onClick={closeModal}>
                ×
              </button>
            </div>
            <div className="dialog-body">
              <div>
                <div className="gallery" {...swipe}>
                  <img
                    src={selected.images[imageIndex]}
                    alt={t("products.imageAlt", {
                      name: selectedCopy.name,
                      current: imageIndex + 1,
                      total: selected.images.length,
                    })}
                  />
                  {selected.images.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="icon-btn nav-arrow prev"
                        aria-label={t("products.previous")}
                        onClick={() => step(-1)}
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        className="icon-btn nav-arrow next"
                        aria-label={t("products.next")}
                        onClick={() => step(1)}
                      >
                        ›
                      </button>
                    </>
                  )}
                </div>
                {selected.images.length > 1 && (
                  <div className="gallery-meta">
                    {selected.images.map((image, index) => (
                      <span
                        key={image}
                        className={index === imageIndex ? "dot is-on" : "dot"}
                      />
                    ))}
                    <span>
                      {imageIndex + 1} / {selected.images.length}
                    </span>
                  </div>
                )}
              </div>
              <div className="dialog-copy">
                <h3 id="product-dialog-title">{selectedCopy.name}</h3>
                <p>{selectedCopy.description}</p>
                <ul className="feature-list">
                  {selectedCopy.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
              </div>
            </div>
          </>
        )}
      </dialog>
    </section>
  );
};

export default Products;
